import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    getCustomerByID,
    getAllDocumentRequestByAccount,
    popup,
    setLocalStorageItem,
} from '../../../utils';
import CSMProfileCard from './CSMProfileCard';
import CustomerTPABox from './CustomerTPABox';
import CustomerDashboardHiringClients from './CustomerDashboardHiringClients';
import CustomerDashboardDocumentRequests from './CustomerDashboardDocumentRequests';
import CustomerFeedback from './CustomerFeedback';
import { setPlatformLength } from '../../../redux';
import { CustomerNewsFeed } from './CustomerNewsFeed';
import { LOCAL_STORAGE, QUERY_KEYS } from '../../../constants';
import { useQuery } from '@tanstack/react-query';
import { getComplianceStatusCards, getComplianceThresholds } from '../../../utils/apiServices/complianceApi';

const DashboardContent = () => {
    const [csmData, setCsmData] = useState({});
    const [tpaData, setTPAData] = useState([]);
    const customerId = useSelector((state) => state.accountArray.selectedAccount);
    const dispatch = useDispatch();

    const customerByIdQuery = useQuery({
        queryKey: [QUERY_KEYS.CUSTOMER_BY_ID, customerId],
        queryFn: async () =>
            await getCustomerByID({
                customerID: customerId,
                fetchCSM: true,
                fetchHiringClient: false,
                fetchPlatform: true,
            }),
        select: (res) => res.data || {},
        enabled: !!customerId,
    });
    if (customerByIdQuery.isError) popup('error', customerByIdQuery.error.message);

    useEffect(() => {
        if (customerByIdQuery?.data) {
            const csmArr = customerByIdQuery.data[0]?.csm || [];
            const firstCsm = Array.isArray(csmArr) ? csmArr[0] : undefined;
            if (firstCsm) {
                setCsmData(firstCsm);
                if (firstCsm?.id) {
                    setLocalStorageItem(LOCAL_STORAGE.CURRENT_CSM, firstCsm.id);
                }
            }
            setTPAData(customerByIdQuery.data[0].platforms);
            dispatch(setPlatformLength(customerByIdQuery?.data[0]?.platforms?.length ?? 0));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerByIdQuery.data]);

    const documentRequestQuery = useQuery({
        queryKey: [QUERY_KEYS.DOCUMENT_REQUEST, customerId, 'DASHBOARD'],
        queryFn: async () =>
            await getAllDocumentRequestByAccount({
                account_id: customerId,
                offset: 1,
                limit: 21,
                filter: { status: ['pending', 'requested', 'rejected'] },
            }),
        select: (res) => ({ data: res.data, count: res.count }),
        placeholderData: { data: [], count: 0 },
        enabled: !!customerId,
    });
    if (documentRequestQuery.isError) popup('error', documentRequestQuery.error.message);

    const complianceQuery = useQuery({
        queryKey: [QUERY_KEYS.COMPLIANCE_STATUS, customerId],
        queryFn: () => getComplianceStatusCards({ account_id: customerId, limit: 5, offset: 1 }),
        select: (data) => ({ enabled: !!data?.data?.enabled, cards: (data?.data?.cards || []) }),
        enabled: !!customerId,
    });

    const thresholdsQuery = useQuery({
        queryKey: [QUERY_KEYS.COMPLIANCE_STATUS, 'thresholds', customerId],
        queryFn: () => getComplianceThresholds(),
        select: (data) => data?.data?.defaults || { greenDays: 30, yellowDays: 14 },
        enabled: !!customerId,
    });

    const handleAskAssistantCompliance = () => {
        try {
            if (!customerId) return;
            const cards = (complianceQuery.data && complianceQuery.data.cards) || [];
            const payload = {
                type: 'compliance_status_card',
                accountId: customerId,
                cardCount: Array.isArray(cards) ? cards.length : 0,
            };
            setLocalStorageItem('ssc_ai_selection', payload);
        } catch (e) {
            // best-effort; do not disrupt dashboard UX
        }
    };

    return (
        <div className='customer-dashboard-view pb-5 pb-md-0'>
            <div className='row main-content'>
                <div className='col-12 col-xl-9 custom-col-set'>
                    <div className='h-100'>
                        <div className=' d-flex flex-column justify-content-between gap-4 mb-32 flex-sm-row px-1 px-lg-0'>
                            <CSMProfileCard csmData={csmData} />
                            <CustomerTPABox
                                tpaData={tpaData}
                                isLoading={customerByIdQuery.isFetching}
                            />
                        </div>
                        <div className='row main-content-inner px-1 px-lg-0'>
                            <CustomerDashboardHiringClients
                                platforms={tpaData}
                                key={customerId}
                                isLoading={customerByIdQuery.isFetching}
                            />
                            <CustomerDashboardDocumentRequests
                                documentRequests={documentRequestQuery.data.data}
                                documentRequestsCount={documentRequestQuery.data.count}
                                showViewAllButton={documentRequestQuery.data.count >= 20}
                                isLoading={documentRequestQuery.isFetching}
                            />
                        </div>
                    </div>
                </div>
                <div className='col-12 col-xl-3 custom-col-set mt-4 mt-xl-0'>
                    <div className='h-100 flex-column d-flex gap-3 align-items-center justify-content-between w-100 ssc-activity-bar px-1 px-lg-0'>
                        <CustomerNewsFeed />
                        <div className='w-100 card-container p-3'>
                            <h5 className='mb-2'>Compliance Status</h5>
                            {complianceQuery.isFetching ? (
                                <div className='text-muted'>Loading…</div>
                            ) : (complianceQuery.data?.enabled && (complianceQuery.data?.cards || []).length) ? (
                                <ul className='list-unstyled m-0'>
                                    {(complianceQuery.data?.cards || []).map((c, idx) => (
                                        <li key={idx} className='mb-2' title={c.reason}>
                                            <div className='fw-semibold'>{c.document_name} — {c.status.toUpperCase()}</div>
                                            <div className='text-muted small'>{c.reason}</div>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <div className='text-muted'>No status available</div>
                            )}
                            {complianceQuery.data?.enabled && !complianceQuery.isFetching && (
                                <div className='text-muted small mt-2'>Thresholds: Green ≥ {thresholdsQuery.data?.greenDays ?? 30} days, Yellow ≥ {thresholdsQuery.data?.yellowDays ?? 14} days</div>
                            )}
                            {complianceQuery.data?.enabled && !complianceQuery.isFetching && (
                                <div className='mt-2 d-flex justify-content-end'>
                                    <button
                                        type='button'
                                        className='ssc-card-btn2 text-nowrap'
                                        onClick={handleAskAssistantCompliance}
                                        title='Prime the AI assistant with this account&apos;s compliance status. Then open the Assistant to review risks and next actions.'
                                    >
                                        Ask Assistant
                                    </button>
                                </div>
                            )}
                        </div>
                        <CustomerFeedback />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DashboardContent;
